import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { MenuPreview } from '@/components/menu/MenuPreview';
import { getPublicMenu, getPublicRestaurant } from '@/lib/firebase/menus';
import { useMenuAnalytics } from '@/lib/services/analytics';
import { applyThemeColor, getThemeColor } from '@/lib/utils/theme';
import type { Restaurant, MenuSection, MenuItem } from '@/types';

interface PublicMenuPageState {
  restaurant: Restaurant | null;
  sections: MenuSection[];
  items: MenuItem[];
  loading: boolean;
  error: string | null;
  notFound: boolean;
}

interface PublicMenuPageProps {
  restaurantId: string;
}

const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 animate-pulse">
        <img 
          src="/logo.svg" 
          alt="Qodrix Menu" 
          className="h-20 w-20 object-contain"
        />
      </div>
      <p className="text-gray-600">Loading menu...</p>
    </div>
  </div>
);

const ErrorState: React.FC<{ message: string; canRetry?: boolean; onRetry?: () => void; isAccessExpired?: boolean }> = ({ 
  message, 
  canRetry = false, 
  onRetry,
  isAccessExpired = false
}) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <div className="text-center max-w-md">
      <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
        isAccessExpired ? 'bg-orange-100' : 'bg-red-100'
      }`}>
        {isAccessExpired ? (
          <AlertCircle className="w-8 h-8 text-orange-600" />
        ) : (
          <AlertCircle className="w-8 h-8 text-red-600" />
        )}
      </div>
      <h1 className="text-xl font-semibold text-gray-900 mb-2">
        {isAccessExpired ? 'Menu Temporarily Unavailable' : 'Oops!'}
      </h1>
      <p className="text-gray-600 mb-6">{message}</p>
      {canRetry && onRetry && (
        <button
          onClick={onRetry}
          className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          Try Again
        </button>
      )}
    </div>
  </div>
);

const NotFoundState: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <div className="text-center max-w-md">
      <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <AlertCircle className="w-8 h-8 text-orange-600" />
      </div>
      <h1 className="text-xl font-semibold text-gray-900 mb-2">Menu Temporarily Unavailable</h1>
      <p className="text-gray-600 mb-6">
        This restaurant's access has expired. The menu will be available again once the restaurant owner renews their access.
      </p>
      <div className="text-sm text-gray-500">
        <p>If you're the restaurant owner, please log in to extend your access.</p>
      </div>
    </div>
  </div>
);
const PublicMenuPageContent: React.FC<PublicMenuPageProps> = ({ restaurantId }) => {
  const [state, setState] = useState<PublicMenuPageState>({
    restaurant: null,
    sections: [],
    items: [],
    loading: true,
    error: null,
    notFound: false,
  });

  const { trackView, trackSessionEnd } = useMenuAnalytics(restaurantId);
  const [viewId, setViewId] = useState<string | null>(null);
  const [sessionStartTime] = useState(Date.now());
  const hasTrackedView = useRef(false);

  // Load menu data
  const loadMenuData = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Fetch restaurant and menu data
      const [restaurant, menuData] = await Promise.all([
        getPublicRestaurant(restaurantId),
        getPublicMenu(restaurantId)
      ]);

      if (!restaurant) {
        setState(prev => ({ ...prev, loading: false, notFound: true }));
        return;
      }

      if (!menuData) {
        setState(prev => ({ 
          ...prev, 
          loading: false, 
          restaurant,
          sections: [],
          items: []
        }));
        return;
      }

      setState(prev => ({ 
        ...prev, 
        loading: false,
        restaurant,
        sections: menuData.sections,
        items: menuData.items
      }));

      // Track menu view for analytics (only once per session)
      if (!hasTrackedView.current) {
        hasTrackedView.current = true;
        const sessionViewId = await trackView();
        setViewId(sessionViewId);
      }

    } catch (error) {
      console.error('Error loading menu data:', error);
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: 'Failed to load menu. Please check your internet connection and try again.'
      }));
    }
  }, [restaurantId, trackView]);

  // Track session end when component unmounts
  useEffect(() => {
    return () => {
      if (viewId && viewId !== 'error') {
        trackSessionEnd(viewId, sessionStartTime);
      }
    };
  }, [viewId, trackSessionEnd, sessionStartTime]);

  // Load data on mount
  useEffect(() => {
    loadMenuData();
  }, [loadMenuData]);

  // Apply theme when restaurant data is available
  useEffect(() => {
    if (state.restaurant) {
      const themeColor = getThemeColor(state.restaurant.themeColor);
      applyThemeColor(themeColor);
    }
  }, [state.restaurant]);

  // Handle loading state
  if (state.loading) {
    return <LoadingSpinner />;
  }

  // Handle not found
  if (state.notFound) {
    return <NotFoundState />;
  }

  // Handle error state
  if (state.error) {
    return (
      <ErrorState 
        message={state.error}
        canRetry={true}
        onRetry={loadMenuData}
      />
    );
  }

  // Handle no restaurant (shouldn't happen due to notFound check above)
  if (!state.restaurant) {
    return <NotFoundState />;
  }

  return (
    <MenuPreview
      restaurant={state.restaurant}
      sections={state.sections}
      items={state.items}
      onBack={() => {
        // For public menu, we don't have a "back" action
        // This could redirect to a home page or do nothing
        window.history.back();
      }}
      showPoweredBy={true}
      isPublicView={true}
      onViewTracked={(analytics) => {
        // Additional analytics tracking if needed
        console.log('Menu view tracked:', analytics);
      }}
    />
  );
};

export const PublicMenuPage: React.FC = () => {
  const { restaurantId } = useParams<{ restaurantId: string }>();

  // Handle missing restaurantId parameter
  if (!restaurantId) {
    return <Navigate to="/" replace />;
  }

  return <PublicMenuPageContent restaurantId={restaurantId} />;
};